/**
 * Dashboard Service
 * Centralizes all dashboard data fetching with error handling and caching
 */

import { authFetch } from '../utils/auth';

// Simple in-memory cache with TTL
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCached(key) {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  cache.delete(key);
  return null;
}

function setCache(key, data) {
  cache.set(key, { data, timestamp: Date.now() });
}

class DashboardService {
  /**
   * Fetch non-critical dashboard data (background)
   * Returns: { conversations, memories, success, errors }
   */
  async fetchBackgroundData() {
    try {
      const [conversationsResult, memoriesResult] = await Promise.allSettled([
        authFetch('/conversations?limit=5&sort=-updatedAt').then(res => res.json()),
        authFetch('/memories?limit=50').then(res => res.json()),
      ]);

      return {
        conversations: conversationsResult.status === 'fulfilled'
          ? conversationsResult.value.conversations || []
          : [],
        memories: memoriesResult.status === 'fulfilled'
          ? memoriesResult.value.memories || []
          : [],
        success: true,
        errors: []
      };
    } catch (error) {
      console.error('Error fetching background dashboard data:', error);
      return {
        conversations: [],
        memories: [],
        success: false,
        errors: [{ type: 'background', error }]
      };
    }
  }

  /**
   * Fetch unified recent activity feed (most recent actions)
   * Combines drafts, conversations, and documents into single timeline
   * Returns: Array of activity items sorted by recency
   */
  async fetchRecentActivity() {
    const cacheKey = 'dashboard:activity';
    const cached = getCached(cacheKey);
    if (cached) return cached;

    try {
      const response = await authFetch('/dashboard/recent-activity');
      const data = await response.json();
      const activities = data.activities || [];
      setCache(cacheKey, activities);
      return activities;
    } catch (error) {
      console.error('Error fetching recent activity:', error);

      // Fallback: construct from existing endpoints
      try {
        const [draftsRes, conversationsRes] = await Promise.allSettled([
          authFetch('/drafts?limit=5').then(res => res.json()),
          authFetch('/conversations?limit=5&sort=-updatedAt').then(res => res.json()),
        ]);

        const activities = [];

        // Add drafts
        if (draftsRes.status === 'fulfilled') {
          const drafts = draftsRes.value.drafts || [];
          drafts.forEach(draft => {
            activities.push({
              type: draft.status === 'downloadable' || draft.status === 'ready' ? 'document' : 'draft',
              id: draft.draftId,
              title: draft.status === 'downloadable'
                ? 'Hjelpestønad søknad (klar)'
                : `Hjelpestønad søknad (${draft.sectionsComplete || 0}% fullført)`,
              timestamp: draft.updatedAt,
              data: draft
            });
          });
        }

        // Add conversations
        if (conversationsRes.status === 'fulfilled') {
          const conversations = conversationsRes.value.conversations || [];
          conversations.forEach(conv => {
            activities.push({
              type: 'conversation',
              id: conv._id,
              title: conv.title || 'Samtale',
              timestamp: conv.updatedAt,
              data: conv
            });
          });
        }

        // Sort by timestamp (most recent first)
        activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        return activities.slice(0, 5);
      } catch (fallbackError) {
        console.error('Fallback activity fetch failed:', fallbackError);
        return [];
      }
    }
  }

  /**
   * Fetch dynamic AI coaching topics based on user context
   * Returns: Array of topic objects with { id, label, description, action }
   */
  async fetchCoachingTopics(profile) {
    const cacheKey = `dashboard:coaching:${profile?.userId}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;

    try {
      const response = await authFetch('/dashboard/coaching-topics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile: {
            childAlias: profile?.child?.alias,
            challenges: profile?.challenges,
            schoolStage: profile?.schoolStage,
            currentBenefits: profile?.currentBenefits
          }
        })
      });

      const data = await response.json();
      const topics = data.topics || [];
      setCache(cacheKey, topics);
      return topics;
    } catch (error) {
      console.error('Error fetching coaching topics:', error);

      // Fallback to rule-based topics
      return this.generateFallbackTopics(profile);
    }
  }

  /**
   * Generate rule-based coaching topics as fallback
   */
  generateFallbackTopics(profile) {
    const topics = [];
    const challenges = profile?.challenges || [];
    const schoolStage = profile?.schoolStage;
    const currentBenefits = profile?.currentBenefits || [];

    // Topic 1: Sleep/Rest (if challenge exists)
    if (challenges.includes('sleep') || challenges.includes('night_care')) {
      topics.push({
        id: 'sleep',
        label: 'Søvn og hvile',
        description: 'Få hjelp med søvnproblemer og nattevakt',
        action: 'start_conversation',
        context: { topic: 'sleep', initialMessage: 'Jeg trenger hjelp med søvn og hvile for barnet mitt' }
      });
    }

    // Topic 2: School support (if school-age)
    if (schoolStage === 'primary' || schoolStage === 'secondary') {
      topics.push({
        id: 'school',
        label: 'Skole og pedagogikk',
        description: 'Skoletilpasning og spesialundervisning',
        action: 'start_conversation',
        context: { topic: 'school', initialMessage: 'Jeg trenger veiledning om skole og opplæring' }
      });
    }

    // Topic 3: Respite care
    if (!currentBenefits.includes('avlastning')) {
      topics.push({
        id: 'respite',
        label: 'Avlastning',
        description: 'Få pauser og avlastning i hverdagen',
        action: 'start_conversation',
        context: { topic: 'respite', initialMessage: 'Jeg vil vite mer om avlastning' }
      });
    }

    // Topic 4: Next benefits
    if (currentBenefits.includes('hjelpestonad') && !currentBenefits.includes('grunnstonad')) {
      topics.push({
        id: 'next_benefit',
        label: 'Neste ytelse: Grunnstønad',
        description: 'Grunnstønad for ekstra utgifter',
        action: 'start_conversation',
        context: { topic: 'grunnstonad', initialMessage: 'Jeg vil vite om jeg kan søke grunnstønad' }
      });
    }

    // NEW STARTER 1 — PLACEHOLDER COPY (Dan to replace label/description/initialMessage)
    topics.push({
      id: 'economy',
      label: 'Økonomi og støtteordninger', // TODO(Dan): final copy
      description: 'Oversikt over økonomisk støtte du kan ha krav på', // TODO(Dan): final copy
      action: 'start_conversation',
      context: { topic: 'economy', initialMessage: 'Hvilke økonomiske støtteordninger kan vi ha krav på?' } // TODO(Dan): final copy
    });

    // NEW STARTER 2 — PLACEHOLDER COPY (Dan to replace label/description/initialMessage)
    topics.push({
      id: 'everyday',
      label: 'Hverdagen hjemme', // TODO(Dan): final copy
      description: 'Tips og støtte for en enklere hverdag', // TODO(Dan): final copy
      action: 'start_conversation',
      context: { topic: 'everyday', initialMessage: 'Jeg trenger råd for å få hverdagen hjemme til å gå opp' } // TODO(Dan): final copy
    });

    // Default topic if nothing else matches
    if (topics.length === 0) {
      topics.push({
        id: 'general',
        label: 'Generell veiledning',
        description: 'Få oversikt over rettigheter og muligheter',
        action: 'start_conversation',
        context: { topic: 'general', initialMessage: 'Jeg lurer på hva slags hjelp som finnes for oss' }
      });
    }

    // Limit to 6 topics (raised from 4 to make room for the 2 new starters)
    return topics.slice(0, 6);
  }

  /**
   * Fetch a random weekly tip from knowledge base
   * Returns: { title, content, source, learnMoreUrl }
   */
  async fetchWeeklyTip() {
    const cacheKey = 'dashboard:weekly-tip';
    const cached = getCached(cacheKey);
    if (cached) return cached;

    try {
      const response = await authFetch('/dashboard/weekly-tip');
      const data = await response.json();
      const tip = data.tip;

      // Cache with longer TTL (24 hours) for tips
      cache.set(cacheKey, { data: tip, timestamp: Date.now(), ttl: 24 * 60 * 60 * 1000 });

      return tip;
    } catch (error) {
      console.error('Error fetching weekly tip:', error);

      // Fallback to hardcoded tips
      return this.getFallbackTip();
    }
  }

  /**
   * Get fallback tip when API fails
   */
  getFallbackTip() {
    const fallbackTips = [
      {
        title: 'Hjelpestønad og skattekort',
        content: 'Hjelpestønad er skattefri. Hvis du mottar hjelpestønad, trenger du ikke føre dette på skattemeldingen din.',
        source: 'Sjekklisten',
        learnMoreUrl: null
      },
      {
        title: 'Når søke hjelpestønad',
        content: 'Du kan søke hjelpestønad når barnet ditt trenger ekstra tilsyn og pleie sammenlignet med jevnaldrende. Det er ingen nedre aldersgrense.',
        source: 'Håndboken',
        learnMoreUrl: null
      },
      {
        title: 'Saksbehandlingstid',
        content: 'Nav har som mål å behandle søknader om hjelpestønad innen 3 måneder. Du kan følge med på saken din i Min side på nav.no.',
        source: 'Sjekklisten',
        learnMoreUrl: null
      }
    ];

    const randomIndex = Math.floor(Math.random() * fallbackTips.length);
    return fallbackTips[randomIndex];
  }

  /**
   * Clear all caches (useful after major actions)
   */
  clearCache() {
    cache.clear();
  }
}

// Export singleton instance
const dashboardServiceInstance = new DashboardService();
export default dashboardServiceInstance;
